import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import Card from '../components/Card'
import Button from '../components/Button'
import {
  Building2,
  Users,
  UserCheck,
  FileText,
  Heart,
  Calendar,
  TrendingUp,
  Activity,
  Settings,
  Eye,
  Clock,
  DollarSign,
  Package,
  Shield,
  AlertTriangle,
  Bug,
  Database,
  Lock,
  CheckCircle2,
  XCircle,
  BarChart3,
} from 'lucide-react'
import {
  getPlatformAnalytics,
  getAllOrganizations,
  getActivityLogs,
  getRecentOrganizations,
} from '../services/masterAdmin'
import { formatDate } from '../utils/helpers'
import { getQueryStats } from '../services/queryOptimization'
import { getErrorStatistics } from '../services/errorTracking'
import { supabase } from '../services/supabase'

const MasterAdminDashboard = () => {
  const [analytics, setAnalytics] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [recentOrgs, setRecentOrgs] = useState([])
  const [activityLogs, setActivityLogs] = useState([])
  const [securityStats, setSecurityStats] = useState(null)
  const [errorStats, setErrorStats] = useState(null)
  const [queryStats, setQueryStats] = useState(null)
  const [complianceStats, setComplianceStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [
        analyticsData,
        orgsData,
        recentData,
        logsData,
        queryStatsData,
      ] = await Promise.all([
        getPlatformAnalytics(),
        getAllOrganizations(),
        getRecentOrganizations(5),
        getActivityLogs(20),
        Promise.resolve(getQueryStats()).catch(() => null),
      ])

      setAnalytics(analyticsData)
      setOrganizations(orgsData)
      setRecentOrgs(recentData)
      setActivityLogs(logsData)
      setQueryStats(queryStatsData)

      // Load platform-wide security and error statistics
      await loadSecurityStats()
      await loadErrorStats()
      await loadComplianceStats()
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      alert('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const loadSecurityStats = async () => {
    try {
      // Get platform-wide security statistics
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_sessions')
        .select('id, is_active, expires_at')
        .eq('is_active', true)

      const { data: breaches, error: breachesError } = await supabase
        .from('breach_events')
        .select('id, severity, created_at')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())

      setSecurityStats({
        activeSessions: sessions?.length || 0,
        recentBreaches: breaches?.length || 0,
        criticalBreaches: breaches?.filter((b) => b.severity === 'critical').length || 0,
      })
    } catch (error) {
      console.error('Error loading security stats:', error)
    }
  }

  const loadErrorStats = async () => {
    try {
      // Get platform-wide error statistics
      const { data: errorAggregations, error } = await supabase
        .from('error_aggregations')
        .select('error_count, severity, last_seen')
        .gte('last_seen', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())

      if (!error && errorAggregations) {
        const totalErrors = errorAggregations.reduce((sum, e) => sum + (e.error_count || 0), 0)
        const criticalErrors = errorAggregations
          .filter((e) => e.severity === 'critical')
          .reduce((sum, e) => sum + (e.error_count || 0), 0)

        setErrorStats({
          totalErrors,
          criticalErrors,
          uniqueErrors: errorAggregations.length,
        })
      }
    } catch (error) {
      console.error('Error loading error stats:', error)
    }
  }

  const loadComplianceStats = async () => {
    try {
      // Get platform-wide compliance statistics
      const { data: reports, error } = await supabase
        .from('compliance_reports')
        .select('report_type, compliance_score, created_at')
        .order('created_at', { ascending: false })
        .limit(100)

      if (!error && reports) {
        const latestHIPAA = reports.find((r) => r.report_type === 'hipaa')
        const latestGDPR = reports.find((r) => r.report_type === 'gdpr')

        setComplianceStats({
          hipaaScore: latestHIPAA?.compliance_score || 0,
          gdprScore: latestGDPR?.compliance_score || 0,
          totalReports: reports.length,
        })
      }
    } catch (error) {
      console.error('Error loading compliance stats:', error)
    }
  }

  const statCards = [
    {
      label: 'Total Organizations',
      value: analytics?.organizations || 0,
      icon: Building2,
      bgColor: 'bg-primary-100',
      iconColor: 'text-primary-600',
    },
    {
      label: 'Total Users',
      value: analytics?.users || 0,
      icon: Users,
      bgColor: 'bg-secondary-100',
      iconColor: 'text-secondary-600',
    },
    {
      label: 'Total Clients',
      value: analytics?.clients || 0,
      icon: UserCheck,
      bgColor: 'bg-accent-100',
      iconColor: 'text-accent-600',
    },
    {
      label: 'Examinations',
      value: analytics?.examinations || 0,
      icon: FileText,
      bgColor: 'bg-primary-100',
      iconColor: 'text-primary-600',
    },
    {
      label: 'Care Plans',
      value: analytics?.carePlans || 0,
      icon: Heart,
      bgColor: 'bg-secondary-100',
      iconColor: 'text-secondary-600',
    },
    {
      label: 'Active Subscriptions',
      value: analytics?.subscriptions?.active || 0,
      icon: DollarSign,
      bgColor: 'bg-accent-100',
      iconColor: 'text-accent-600',
    },
  ]

  const subscriptionBreakdown = [
    { label: 'Lite', value: analytics?.subscriptions?.lite || 0, color: 'teal' },
    { label: 'Essential', value: analytics?.subscriptions?.essential || 0, color: 'blue' },
    { label: 'Professional', value: analytics?.subscriptions?.professional || 0, color: 'green' },
    { label: 'Enterprise', value: analytics?.subscriptions?.enterprise || 0, color: 'purple' },
    { label: 'White Label', value: analytics?.subscriptions?.white_label || 0, color: 'indigo' },
    { label: 'Demo', value: analytics?.subscriptions?.demo || 0, color: 'gray' },
  ]

  if (loading) {
    return (
      <div className="section-container">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Loading master admin dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
            <Settings size={24} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Master Admin Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400">Platform-wide analytics and organization management</p>
          </div>
        </div>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card variant="soft" hover className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value.toLocaleString()}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                    <Icon className={stat.iconColor} size={24} />
                  </div>
                </div>
              </Card>
            </motion.div>
          )
        })}
      </div>

      {/* Subscription Breakdown */}
      <Card variant="soft" className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <TrendingUp size={20} />
          Subscription Breakdown
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {subscriptionBreakdown.map((sub) => (
            <div key={sub.label} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{sub.label}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{sub.value}</p>
            </div>
          ))}
        </div>
      </Card>

      {/* Security & Management Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="soft" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Shield size={20} />
              Security
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Active Sessions</span>
              <span className="font-semibold text-gray-900 dark:text-white">{securityStats?.activeSessions || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Recent Breaches</span>
              <span className={`font-semibold ${securityStats?.recentBreaches > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {securityStats?.recentBreaches || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Critical</span>
              <span className={`font-semibold ${securityStats?.criticalBreaches > 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                {securityStats?.criticalBreaches || 0}
              </span>
            </div>
          </div>
        </Card>

        <Card variant="soft" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Bug size={20} />
              Errors (24h)
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Errors</span>
              <span className="font-semibold text-gray-900 dark:text-white">{errorStats?.totalErrors || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Critical</span>
              <span className={`font-semibold ${errorStats?.criticalErrors > 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                {errorStats?.criticalErrors || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Unique</span>
              <span className="font-semibold text-gray-900 dark:text-white">{errorStats?.uniqueErrors || 0}</span>
            </div>
          </div>
        </Card>

        <Card variant="soft" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <BarChart3 size={20} />
              Performance
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Queries</span>
              <span className="font-semibold text-gray-900 dark:text-white">{queryStats?.total || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Slow Queries</span>
              <span className={`font-semibold ${queryStats?.slow > 10 ? 'text-yellow-600' : 'text-gray-900 dark:text-white'}`}>
                {queryStats?.slow || 0}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Avg Duration</span>
              <span className="font-semibold text-gray-900 dark:text-white">{queryStats?.avgDuration?.toFixed(0) || 0}ms</span>
            </div>
          </div>
        </Card>

        <Card variant="soft" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Lock size={20} />
              Compliance
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">HIPAA Score</span>
              <span className={`font-semibold ${complianceStats?.hipaaScore >= 90 ? 'text-green-600' : complianceStats?.hipaaScore >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                {complianceStats?.hipaaScore || 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">GDPR Score</span>
              <span className={`font-semibold ${complianceStats?.gdprScore >= 90 ? 'text-green-600' : complianceStats?.gdprScore >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                {complianceStats?.gdprScore || 0}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Reports</span>
              <span className="font-semibold text-gray-900 dark:text-white">{complianceStats?.totalReports || 0}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="soft" hover className="p-6 cursor-pointer" onClick={() => navigate('/master-admin/organizations')}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-blue-500 flex items-center justify-center">
              <Building2 size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organizations</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Manage all organizations</p>
            </div>
          </div>
        </Card>

        <Card variant="soft" hover className="p-6 cursor-pointer" onClick={() => navigate('/master-admin/features')}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center">
              <Package size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Feature Management</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Manage features and subscription tiers</p>
            </div>
          </div>
        </Card>

        <Card variant="soft" hover className="p-6 cursor-pointer" onClick={() => navigate('/errors')}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center">
              <AlertTriangle size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Error Dashboard</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">View error logs and statistics</p>
            </div>
          </div>
        </Card>

        <Card variant="soft" hover className="p-6 cursor-pointer" onClick={() => navigate('/compliance')}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center">
              <Shield size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Compliance</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">View compliance reports and status</p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Organizations */}
        <Card variant="soft" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Building2 size={20} />
              Recent Organizations
            </h2>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/master-admin/organizations')}
            >
              View All
            </Button>
          </div>
          {recentOrgs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No organizations yet</p>
          ) : (
            <div className="space-y-3">
              {recentOrgs.map((org) => (
                <div
                  key={org.id}
                  className="p-3 rounded-xl bg-white/60 border border-gray-100 hover:border-primary-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/master-admin/organizations/${org.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{org.name}</p>
                      <p className="text-xs text-gray-500">
                        {org.subscription_tier || 'demo'} • Created {formatDate(org.created_at)}
                      </p>
                    </div>
                    <Eye size={18} className="text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Activity Log */}
        <Card variant="soft" className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Activity size={20} />
            Recent Activity
          </h2>
          {activityLogs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No activity logged yet</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {activityLogs.map((log) => {
                const isUpgrade = log.action_type?.includes('subscription_upgrade')
                const isCompleted = log.action_type?.includes('completed')
                return (
                  <div
                    key={log.id}
                    className={`p-3 rounded-xl border ${
                      isUpgrade
                        ? isCompleted
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-white/60 dark:bg-gray-800/60 border-gray-100 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {isUpgrade && (
                          <DollarSign
                            size={14}
                            className={isCompleted ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}
                          />
                        )}
                        <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                          {log.action_type.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Clock size={12} />
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        {isUpgrade && log.details.organization_name && (
                          <p className="font-semibold mb-1">
                            Organization: {log.details.organization_name}
                          </p>
                        )}
                        {isUpgrade && log.details.current_tier && log.details.new_tier && (
                          <p className="mb-1">
                            Upgrade: {log.details.current_tier} → {log.details.new_tier_name || log.details.new_tier}
                          </p>
                        )}
                        {isUpgrade && log.details.amount && (
                          <p className="mb-1">
                            Amount: {log.details.currency || 'NGN'} {log.details.amount}
                          </p>
                        )}
                        {!isUpgrade && (
                          <p>{JSON.stringify(log.details, null, 2).slice(0, 100)}...</p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default MasterAdminDashboard

